import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { FormlyField, FormlyFieldConfig } from '@ngx-formly/core';
import { Select, Store } from '@ngxs/store';
import { FormlyDialogData } from 'app/components/formly-dialog/formly-dialog.component';
import { FormlyModel } from 'app/form-core/formly/formly-core.config';
import { ContactRelationship } from 'app/models/dto/contact-relationship.model';
import { ErcContact } from 'app/models/dto/erc-contact.model';
import { ContactService } from 'app/services/contact/contact.service';
import { ErcService } from 'app/services/erc/erc.service';
import { CreateContactEmployeeRelationship, CreateContactOwnerRelationship, DeleteContactRelationship, UpdateContactEmployeeRelationship, UpdateContactOwnerRelationship, UpdateErcContact } from 'app/store/contact/contact.actions';
import { ContactState } from 'app/store/contact/contact.state';
import _ from 'lodash';
import { Observable, Subject, concat, finalize, forkJoin, map, of, switchMap, take, takeUntil, tap, toArray } from 'rxjs';

export interface DataListItem {
  label: string;
  value: any;
  alias: string;
  sectionType: string;
  cssClass?: string;
  metadata?: {
    extraLabel?: string;
    cssClass?: string;
    pipe?: string;
  };
}

@Component({
  selector: 'app-erc-custom-values-smart',
  templateUrl: './erc-custom-values-smart.component.html',
  styleUrls: ['./erc-custom-values-smart.component.scss'],
  standalone: false
})
export class ErcCustomValuesSmartComponent implements OnInit, OnDestroy {
  @Input() title: string;
  @Input() toggleTitle: string;
  @Input() displayLimit: number;
  @Input() borrowerId: number;

  dataList$ = new Subject<DataListItem[]>();
  destroyed$ = new Subject<boolean>();
  dataListErcContacts: DataListItem[] | null;
  dataListContactEmployeeRelationships: DataListItem[] | null;
  dataListContactOwnerRelationships: DataListItem[] | null;
  ercContacts: ErcContact[];
  contactEmployeeRelationships: ContactRelationship[];
  contactOwnerRelationships: ContactRelationship[];
  loading = true;
  // add typing and pass observable in as a
  @Select(ContactState.ercContacts) ercContacts$: Observable<ErcContact[]>;
  @Select(ContactState.contactEmployeeRelationships) contactEmployeeRelationships$: Observable<ContactRelationship[]>;
  @Select(ContactState.contactOwnerRelationships) contactOwnerRelationships$: Observable<ContactRelationship[]>;

  constructor(public ercService: ErcService, public contactService: ContactService, public store: Store) {}

  ngOnInit() {
    this.ercContacts$.pipe(takeUntil(this.destroyed$)).subscribe((ercContacts) => {
      if (ercContacts.length) {
        this.dataListErcContacts = this.contactService.ercContactToDataList(ercContacts);
        this.ercContacts = ercContacts;
        this.loading = false;
      }
    });

    this.contactEmployeeRelationships$.pipe(takeUntil(this.destroyed$)).subscribe((employeeRelationships) => {
        this.dataListContactEmployeeRelationships = [...this.ercService.relationshipsToDataList(employeeRelationships)];
        this.contactEmployeeRelationships = employeeRelationships;
        this.loading = false;
    });

    this.contactOwnerRelationships$.pipe(takeUntil(this.destroyed$)).subscribe((ownerRelationships) => {
        this.dataListContactOwnerRelationships = [...this.ercService.ownerRelationshipsToDataList(ownerRelationships)];
        this.contactOwnerRelationships = ownerRelationships;
        this.loading = false;
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  ercContactsToFormlyDialogData(
    dataList: DataListItem[],
    formlyDialogDataCallBack: (model: FormlyModel, fields: FormlyFieldConfig[]) => FormlyDialogData
    ): FormlyDialogData {
    const model = this.ercService.ownershipToFormlyModel(dataList);
    const fields = this.ercService.ownershipToFormlyFieldConfigs(dataList);
    return formlyDialogDataCallBack(model, fields);
  }

  employeeRelationshipsToDialogData(
    dataList: DataListItem[],
    formlyDialogDataCallback: (model: FormlyModel, fields: FormlyFieldConfig[]) => FormlyDialogData
  ): FormlyDialogData {
    const model = this.ercService.relationshipToFormlyModel(dataList);
    const fields = this.ercService.relationshipToFormlyFieldConfigs(this.ercContacts, 'employee');
    return formlyDialogDataCallback(model, fields);
  }

  ownerRelationshipsToDialogData(
    dataList: DataListItem[],
    formlyDialogDataCallback: (model: FormlyModel, fields: FormlyFieldConfig[]) => FormlyDialogData
  ): FormlyDialogData {
    const model = this.ercService.ownerRelationshipToFormlyModel(dataList);
    const fields = this.ercService.relationshipToFormlyFieldConfigs(this.ercContacts, 'owner');
    return formlyDialogDataCallback(model, fields);
  }

  ercContactsSaveModel(model: FormlyModel, data: any) {
    return this.ercContacts$.pipe(
      take(1),
      map(ercContacts => this.ercService.formlyModelToErcContacts(ercContacts, model)),
      switchMap((ercContacts) => {
        const requests: Observable<any>[] = [];
        ercContacts.forEach((ercContact) => {
          requests.push(this.store.dispatch(new UpdateErcContact(ercContact)));
        });
        return forkJoin(requests);
      })
    );
  }

  relationshipsEmployeeSaveModel(model: any, data: any) {
    return this.contactEmployeeRelationships$.pipe(
      take(1),
      map(relationships => this.ercService.formlyModelToRelationships(relationships, model)),
      switchMap((relationships) => {
        const convertedNewRelationshipRequests = this.ercService.formlyModelNewToOwnerRelationships(model);
        let newRelationshipRequests: Observable<any>[] = [];

        if (convertedNewRelationshipRequests &&  convertedNewRelationshipRequests.length) {
          newRelationshipRequests = this.ercService.formlyModelNewToRelationships(model)
            .reduce((newRelationshipRequestsAcc: any, relationship) => {
              newRelationshipRequestsAcc.push(this.store.dispatch(new CreateContactEmployeeRelationship(relationship, this.borrowerId)));
              return newRelationshipRequestsAcc;
            }, []);
        }

        const requests: Observable<any>[] = [...newRelationshipRequests];
        relationships.forEach((relationship) => {
          if(this.ercService.determineDeletedRelationships(model, relationship)) {
            requests.push(this.store.dispatch(new DeleteContactRelationship(relationship, 'contactEmployeeRelationships')));
          } else {
            requests.push(this.store.dispatch(new UpdateContactEmployeeRelationship(relationship)));
          }
        });

        return concat(requests).pipe(toArray());
      })
    );
  }

  relationshipsOwnerSaveModel(model: any, data: any) {
    return this.contactOwnerRelationships$.pipe(
      take(1),
      map(relationships => this.ercService.formlyModelToOwnerRelationships(relationships, model)),
      switchMap((relationships) => {
        const convertedNewRelationshipRequests = this.ercService.formlyModelNewToOwnerRelationships(model);
        let newRelationshipRequests: Observable<any>[] = [];
        if (convertedNewRelationshipRequests &&  convertedNewRelationshipRequests.length) {
          newRelationshipRequests = convertedNewRelationshipRequests
          .reduce((newRelationshipRequestsAcc: any, relationship) => {
            newRelationshipRequestsAcc.push(this.store.dispatch(new CreateContactOwnerRelationship(relationship, this.borrowerId)));
            return newRelationshipRequestsAcc;
          }, []);
        }
        const requests: Observable<any>[] = [...newRelationshipRequests];
        relationships.forEach((relationship) => {
          if(this.ercService.determineDeletedRelationships(model, relationship)) {
            requests.push(this.store.dispatch(new DeleteContactRelationship(relationship, 'contactOwnerRelationships')));
          } else {
            requests.push(this.store.dispatch(new UpdateContactOwnerRelationship(relationship)));
          }
        });

        return concat(requests).pipe(toArray());
      })
    );
  }
}
